import { Link } from "@tanstack/react-router";
import { ChevronRightIcon } from "lucide-react";
import { useId } from "react";
import { TeamChip } from "@/components/app/team-chip";
import { Button } from "@/components/ui/button";
import { useT } from "@/i18n";
import { cn } from "@/lib/utils";
import type { Division, Team } from "../../../shared/types";
import { divisionTagOf } from "../../i18n/labels";

/**
 * The viewer's own squads at the right end of the board's DESKTOP tab row
 * (`SectionNav.trailing`, GLASS.md §5, 2026-09-15). A phone lists them in the
 * section sheet instead («Ваші команди», each row led by the same chip): rows
 * of them under the selector pushed the content down.
 *
 * One squad shares the selector's row when it fits: an outline button with
 * the kit chip and «Ваша команда». Several go under the selector as a
 * labelled («Ваші команди») row of equal
 * tiles, so a session that resolves late never moves the selector under a
 * thumb. The chip, never the 40-character name (that is the accessible name
 * and the tooltip), and OUTLINE, never the white pill, which on this ground
 * reads as the selected tab (§4).
 *
 * Two squads with the same code (and often the same kit: one club's Open and
 * Women's squads) would be identical chips, so then every tile says its team
 * name, one tile per line on a phone so the whole name shows; two with the
 * same name as well add their division («Вовчиці · WMN»).
 */
/** A held squad's name as a shortcut shows it: with its division when another
 *  held squad has the same name, or two rows would read the same. */
export function heldTeamLabel(
  team: Team,
  teams: readonly Team[],
  divisions: readonly Division[],
): string {
  const twin = teams.some((x) => x !== team && x.name === team.name);
  const division = twin ? divisionTagOf([...divisions], team.divisionId) : undefined;
  return division === undefined ? team.name : `${team.name} · ${division}`;
}

export function TeamWorkspaceShortcuts({
  slug,
  teams,
  divisions = [],
}: {
  slug: string;
  teams: readonly Team[];
  divisions?: readonly Division[];
}) {
  const t = useT();
  const labelId = useId();
  const told = (team: Team): string => heldTeamLabel(team, teams, divisions);
  const name = (team: Team): string =>
    t("board.team.yourTeamLink", { short: team.short, team: told(team) });
  const link = (team: Team) => <Link to="/t/$slug/team/$club" params={{ slug, club: team.id }} />;
  const [only] = teams;
  if (teams.length === 1 && only !== undefined) {
    return (
      <Button
        variant="outline"
        className="min-w-0 flex-auto adm:ml-auto adm:flex-none"
        render={link(only)}
        aria-label={name(only)}
        title={name(only)}
      >
        <TeamChip short={only.short} color={only.color} size="md" />
        <span>{t("board.team.yourTeam")}</span>
        <ChevronRightIcon aria-hidden="true" />
      </Button>
    );
  }
  const clash = new Set(teams.map((team) => team.short)).size < teams.length;
  return (
    // A fieldset, as the team form's grouped controls: one labelled set of
    // ways in. `min-w-0` undoes a fieldset's min-content floor, which would
    // push three tiles past a narrow phone.
    <fieldset
      aria-labelledby={labelId}
      className="flex min-w-0 basis-full flex-col gap-1.5 adm:ml-auto adm:basis-auto adm:flex-row adm:items-center adm:gap-3"
    >
      <div id={labelId} className="eyebrow text-muted-foreground">
        {t("board.team.yourTeams")}
      </div>
      <div
        className={cn(
          "grid gap-2 adm:flex adm:flex-wrap",
          clash ? "grid-cols-1" : "grid-cols-[repeat(auto-fit,minmax(6.5rem,1fr))]",
        )}
      >
        {teams.map((team) => (
          <Button
            key={team.id}
            variant="outline"
            className="min-w-0"
            render={link(team)}
            aria-label={name(team)}
            title={name(team)}
          >
            <TeamChip short={team.short} color={team.color} size="md" />
            {clash && <span className="min-w-0 truncate">{told(team)}</span>}
            {/* Three tiles share a phone's width: the chevron goes first. */}
            <ChevronRightIcon
              aria-hidden="true"
              className={teams.length > 2 && !clash ? "hidden adm:block" : undefined}
            />
          </Button>
        ))}
      </div>
    </fieldset>
  );
}
